import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Monitor, Building2, LayoutGrid } from "lucide-react";

export default function AdminSpaces() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [devices, setDevices] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpace, setEditingSpace] = useState(null);
  const [occupancyDialogOpen, setOccupancyDialogOpen] = useState(false);
  const [occupancySpace, setOccupancySpace] = useState(null);
  const [occupancyForm, setOccupancyForm] = useState({
    is_occupied: false,
    tenant_member_id: "",
    contract_start: "",
    contract_end: "",
  });
  const [form, setForm] = useState({
    name: "", type: "meeting_room", capacity: 10, description: "",
    amenities: "", photos: "",
    opening_start: "08:00", opening_end: "22:00",
    hourly: 50, daily: 300, monthly: 5000, guest_allowed: true
  });
  const [allLayouts, setAllLayouts] = useState([]);
  const [spaceLayouts, setSpaceLayouts] = useState({});
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false);
  const [layoutSpace, setLayoutSpace] = useState(null);
  const [editLayoutDialog, setEditLayoutDialog] = useState(false);
  const [editingLayout, setEditingLayout] = useState(null);
  const [layoutForm, setLayoutForm] = useState({ name: "", description: "" });

  useEffect(() => { if (!isAdmin) navigate("/login"); else loadData(); }, [isAdmin]);

  const loadData = async () => {
    try {
      const [spacesRes, devicesRes, membersRes, layoutsRes] = await Promise.all([
        api("get", "/spaces"),
        api("get", "/display-devices"),
        api("get", "/admin/members"),
        api("get", "/layouts"),
      ]);
      setSpaces(spacesRes.data);
      setDevices(devicesRes.data);
      setMembers(membersRes.data || []);
      setAllLayouts(layoutsRes.data || []);
      // Load space layouts for each meeting room
      const meetingRooms = spacesRes.data.filter(s => s.type === "meeting_room");
      const slMap = {};
      await Promise.all(meetingRooms.map(async (s) => {
        try {
          const res = await api("get", `/spaces/${s.id}/layouts`);
          slMap[s.id] = res.data || [];
        } catch { slMap[s.id] = []; }
      }));
      setSpaceLayouts(slMap);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      type: form.type,
      capacity: parseInt(form.capacity),
      description: form.description,
      amenities: form.amenities.split(",").map(a => a.trim()).filter(Boolean),
      photos: form.photos.split(",").map(p => p.trim()).filter(Boolean),
      opening_hours: { start: form.opening_start, end: form.opening_end },
      pricing: { hourly: parseFloat(form.hourly), daily: parseFloat(form.daily), monthly: parseFloat(form.monthly) },
      guest_allowed: form.guest_allowed,
    };
    try {
      if (editingSpace) {
        await api("put", `/spaces/${editingSpace.id}`, payload);
        toast.success("Space updated");
      } else {
        await api("post", "/spaces", payload);
        toast.success("Space created");
      }
      setDialogOpen(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (s) => {
    setEditingSpace(s);
    setForm({
      name: s.name, type: s.type, capacity: s.capacity, description: s.description || "",
      amenities: s.amenities?.join(", ") || "",
      photos: s.photos?.join(", ") || "",
      opening_start: s.opening_hours?.start || "08:00",
      opening_end: s.opening_hours?.end || "22:00",
      hourly: s.pricing?.hourly || 0, daily: s.pricing?.daily || 0, monthly: s.pricing?.monthly || 0,
      guest_allowed: s.guest_allowed !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this space?")) return;
    try { await api("delete", `/spaces/${id}`); toast.success("Deleted"); loadData(); }
    catch { toast.error("Failed"); }
  };

  const openNew = () => {
    setEditingSpace(null);
    setForm({ name: "", type: "meeting_room", capacity: 10, description: "", amenities: "", photos: "", opening_start: "08:00", opening_end: "22:00", hourly: 50, daily: 300, monthly: 5000, guest_allowed: true });
    setDialogOpen(true);
  };

  const openOccupancy = (s) => {
    setOccupancySpace(s);
    setOccupancyForm({
      is_occupied: s.occupancy?.is_rented_out || false,
      tenant_member_id: s.occupancy?.tenant_member_id || "",
      contract_start: s.occupancy?.contract_start || "",
      contract_end: s.occupancy?.contract_end || "",
    });
    setOccupancyDialogOpen(true);
  };

  const saveOccupancy = async () => {
    try {
      await api("put", `/admin/spaces/${occupancySpace.id}/occupancy`, {
        is_occupied: occupancyForm.is_occupied,
        tenant_member_id: occupancyForm.is_occupied ? occupancyForm.tenant_member_id : null,
        contract_start: occupancyForm.is_occupied ? occupancyForm.contract_start : null,
        contract_end: occupancyForm.is_occupied ? occupancyForm.contract_end : null,
      });
      toast.success(occupancyForm.is_occupied ? "Space marked as rented out" : "Space marked as available");
      setOccupancyDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update occupancy");
    }
  };


  const toggleLayout = async (spaceId, layoutId, currentlyEnabled) => {
    try {
      const capacity = currentlyEnabled ? 0 : 10;
      const res = await api("post", `/spaces/${spaceId}/toggle-layout?layout_id=${layoutId}&capacity=${capacity}`);
      toast.success(res.data.message);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateLayoutCapacity = async (slId, newCapacity) => {
    try {
      await api("put", `/space-layouts/${slId}?capacity=${newCapacity}`);
      toast.success("Capacity updated");
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveLayoutEdit = async () => {
    if (!editingLayout) return;
    try {
      await api("put", `/layouts/${editingLayout.id}`, layoutForm);
      toast.success("Layout updated");
      setEditLayoutDialog(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const getDeviceForSpace = (spaceId) => devices.find(d => d.space_id === spaceId);

  if (loading) return <div className="max-w-6xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 animate-fade-in" data-testid="admin-spaces">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl">Manage Spaces</h1>
          <p className="text-muted-foreground mt-1">Add, edit, or remove bookable spaces</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-foreground text-background rounded-none" data-testid="add-space-btn">
              <Plus className="h-4 w-4 mr-2" />Add Space
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="space-dialog">
            <DialogHeader>
              <DialogTitle>{editingSpace ? "Edit Space" : "Add New Space"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="space-name-input" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="space-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="meeting_room">Meeting Room</SelectItem><SelectItem value="office">Office</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} data-testid="space-capacity-input" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="space-desc-input" /></div>
              <div><Label>Amenities (comma-separated)</Label><Input value={form.amenities} onChange={e => setForm({ ...form, amenities: e.target.value })} placeholder="WiFi, Projector, Whiteboard" data-testid="space-amenities-input" /></div>
              <div><Label>Photo URLs (comma-separated)</Label><Input value={form.photos} onChange={e => setForm({ ...form, photos: e.target.value })} data-testid="space-photos-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Opens</Label><Input value={form.opening_start} onChange={e => setForm({ ...form, opening_start: e.target.value })} data-testid="space-open-input" /></div>
                <div><Label>Closes</Label><Input value={form.opening_end} onChange={e => setForm({ ...form, opening_end: e.target.value })} data-testid="space-close-input" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>XCG/hr</Label><Input type="number" value={form.hourly} onChange={e => setForm({ ...form, hourly: e.target.value })} data-testid="space-hourly-input" /></div>
                <div><Label>XCG/day</Label><Input type="number" value={form.daily} onChange={e => setForm({ ...form, daily: e.target.value })} data-testid="space-daily-input" /></div>
                <div><Label>XCG/mo</Label><Input type="number" value={form.monthly} onChange={e => setForm({ ...form, monthly: e.target.value })} data-testid="space-monthly-input" /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.guest_allowed} onCheckedChange={v => setForm({ ...form, guest_allowed: v })} data-testid="space-guest-switch" />
                <Label>Guests Allowed</Label>
              </div>
              <Button onClick={handleSave} className="w-full bg-foreground text-background rounded-none" data-testid="save-space-btn">{editingSpace ? "Update" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border overflow-x-auto" data-testid="spaces-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Capacity</TableHead>
            <TableHead>Pricing</TableHead><TableHead>Access</TableHead><TableHead>Tenancy</TableHead><TableHead>Display</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {spaces.map(s => {
              const device = getDeviceForSpace(s.id);
              const isRented = s.occupancy?.is_rented_out;
              return (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{s.type === "meeting_room" ? "Meeting" : "Office"}</Badge></TableCell>
                  <TableCell><Users className="h-3 w-3 inline mr-1" />{s.capacity}</TableCell>
                  <TableCell className="text-xs"><span className="font-medium">XCG {s.pricing?.hourly}/hr</span> | XCG {s.pricing?.daily}/day</TableCell>
                  <TableCell>{s.guest_allowed ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Public</Badge> : <Badge className="bg-amber-100 text-amber-700 text-xs">Members</Badge>}</TableCell>
                  <TableCell>
                    {isRented ? (
                      <div>
                        <Badge className="bg-red-100 text-red-700 text-xs mb-1 block w-fit">Rented Out</Badge>
                        <p className="text-xs text-muted-foreground">{s.occupancy?.tenant_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{s.occupancy?.contract_end ? `Until ${s.occupancy.contract_end}` : ""}</p>
                      </div>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Available</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {device ? (
                      <a href={`/display/${s.id}?token=${device.token}`} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs flex items-center gap-1" data-testid={`display-link-${s.id}`}>
                        <Monitor className="h-3 w-3" />View
                      </a>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openOccupancy(s)} title="Manage Tenancy" data-testid={`occupancy-btn-${s.id}`}>
                        <Building2 className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(s)} data-testid={`edit-space-${s.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(s.id)} data-testid={`delete-space-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {spaces.length === 0 && <div className="p-12 text-center text-muted-foreground">No spaces. Seed data first.</div>}
      </div>

      {/* Display Screen Links */}
      {devices.length > 0 && (
        <Card className="p-4 border" data-testid="display-links-card">
          <h3 className="font-heading font-semibold text-sm mb-3">Display Screen Links</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {spaces.map(s => {
              const device = devices.find(d => d.space_id === s.id);
              if (!device) return null;
              const url = `${window.location.origin}/display/${s.id}?token=${device.token}`;
              return (
                <div key={s.id} className="flex items-center gap-2 text-xs p-2 bg-muted/30 rounded border">
                  <Monitor className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="font-medium truncate">{s.name}</span>
                  <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto text-[10px]" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied!"); }} data-testid={`copy-display-${s.id}`}>
                    Copy Link
                  </Button>
                  <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-[10px]">Open</a>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 text-xs p-2 bg-primary/5 rounded border border-primary/20">
            <LayoutGrid className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="font-semibold">All Rooms Overview</span>
            <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto text-[10px]" onClick={() => { const url = `${window.location.origin}/display-overview?token=${devices[0]?.token}`; navigator.clipboard.writeText(url); toast.success("Overview link copied!"); }} data-testid="copy-overview-link">
              Copy Link
            </Button>
            <a href={`/display-overview?token=${devices[0]?.token}`} target="_blank" rel="noreferrer" className="text-primary hover:underline text-[10px]" data-testid="overview-link">Open</a>
          </div>
        </Card>
      )}

      {/* Layout Management */}
      <Card className="p-5 border" data-testid="layout-management-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-sm">Room Layouts</h3>
          <Dialog open={editLayoutDialog} onOpenChange={setEditLayoutDialog}>
            <DialogContent className="max-w-sm" data-testid="edit-layout-dialog">
              <DialogHeader><DialogTitle>Edit Layout</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>Name</Label><Input value={layoutForm.name} onChange={e => setLayoutForm({...layoutForm, name: e.target.value})} data-testid="edit-layout-name" /></div>
                <div><Label>Description</Label><Input value={layoutForm.description} onChange={e => setLayoutForm({...layoutForm, description: e.target.value})} data-testid="edit-layout-desc" /></div>
                <Button onClick={saveLayoutEdit} className="w-full bg-foreground text-background rounded-none" data-testid="save-layout-edit-btn">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Global layout list with edit */}
        <div className="flex flex-wrap gap-2 mb-4">
          {allLayouts.map(l => (
            <Badge key={l.id} variant="outline" className="py-1.5 px-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => { setEditingLayout(l); setLayoutForm({ name: l.name, description: l.description || "" }); setEditLayoutDialog(true); }}
              data-testid={`layout-badge-${l.id}`}
            >
              <Pencil className="h-3 w-3 mr-1.5 text-muted-foreground" />{l.name}
            </Badge>
          ))}
        </div>

        {/* Per-space layout toggles */}
        {spaces.filter(s => s.type === "meeting_room").map(space => {
          const sLayouts = spaceLayouts[space.id] || [];
          return (
            <div key={space.id} className="border-t pt-3 mt-3" data-testid={`space-layouts-${space.id}`}>
              <p className="font-medium text-sm mb-2">{space.name}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {allLayouts.map(layout => {
                  const sl = sLayouts.find(s => s.layout_id === layout.id);
                  const enabled = !!sl;
                  return (
                    <div key={layout.id} className={`flex items-center gap-2 p-2 rounded border text-xs transition-colors ${enabled ? "bg-muted/50 border-border" : "bg-background border-dashed border-muted-foreground/20"}`}>
                      <Switch
                        checked={enabled}
                        onCheckedChange={() => toggleLayout(space.id, layout.id, enabled)}
                        data-testid={`toggle-layout-${space.id}-${layout.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <span className={`font-medium ${enabled ? "" : "text-muted-foreground"}`}>{layout.name}</span>
                        {enabled && sl && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Users className="h-3 w-3 text-muted-foreground" />
                            <Input
                              type="number" min={1} className="h-5 w-14 text-xs p-0.5 text-center"
                              defaultValue={sl.capacity}
                              onBlur={e => { const v = parseInt(e.target.value); if (v && v !== sl.capacity) updateLayoutCapacity(sl.id, v); }}
                              data-testid={`capacity-${space.id}-${layout.id}`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </Card>

      {/* Occupancy Dialog */}
      <Dialog open={occupancyDialogOpen} onOpenChange={setOccupancyDialogOpen}>
        <DialogContent className="max-w-md" data-testid="occupancy-dialog">
          <DialogHeader>
            <DialogTitle>Manage Tenancy — {occupancySpace?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={occupancyForm.is_occupied}
                onCheckedChange={v => setOccupancyForm({ ...occupancyForm, is_occupied: v })}
                data-testid="occupancy-toggle"
              />
              <Label className="font-medium">
                {occupancyForm.is_occupied ? "Rented Out / Occupied" : "Available"}
              </Label>
            </div>

            {occupancyForm.is_occupied && (
              <>
                <div>
                  <Label>Tenant Member</Label>
                  <Select
                    value={occupancyForm.tenant_member_id}
                    onValueChange={v => setOccupancyForm({ ...occupancyForm, tenant_member_id: v })}
                  >
                    <SelectTrigger data-testid="tenant-select">
                      <SelectValue placeholder="Select a member…" />
                    </SelectTrigger>
                    <SelectContent>
                      {members.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} ({m.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Contract Start</Label>
                    <Input
                      type="date"
                      value={occupancyForm.contract_start}
                      onChange={e => setOccupancyForm({ ...occupancyForm, contract_start: e.target.value })}
                      data-testid="contract-start-input"
                    />
                  </div>
                  <div>
                    <Label>Contract End</Label>
                    <Input
                      type="date"
                      value={occupancyForm.contract_end}
                      onChange={e => setOccupancyForm({ ...occupancyForm, contract_end: e.target.value })}
                      data-testid="contract-end-input"
                    />
                  </div>
                </div>
                {occupancyForm.contract_end && (
                  <p className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Admins will receive email reminders at <strong>6 months</strong>, <strong>3 months</strong>, and <strong>1 month</strong> before the contract ends.
                  </p>
                )}
              </>
            )}

            <Button
              onClick={saveOccupancy}
              className="w-full bg-foreground text-background rounded-none"
              data-testid="save-occupancy-btn"
            >
              {occupancyForm.is_occupied ? "Mark as Rented Out" : "Mark as Available"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
